import { ADMIN_PATHS } from '@norum/contracts';
import { ApiError, failureFrom } from './errors';

/**
 * The admin door (ADR-0073): `POST /v1/admin/<path>`, superadmin only.
 *
 * A second client rather than a flag on `post`, because the two doors have
 * different locks and a screen should not be able to reach one by
 * misspelling the other. The path is typed from the platform's own
 * allow-list, checked at module load like `appPath`. Nothing here sends
 * `humanId` or `actor`; the gateway writes both from the session and the
 * database re-derives the superadmin.
 */
export type AdminPath = string & { readonly __adminPath: unique symbol };

export function adminPath(path: string): AdminPath {
  if (!ADMIN_PATHS.has(path)) {
    throw new Error(`${path} is not a control a superadmin reaches (packages/contracts/src/app-paths.ts, ADMIN_PATHS).`);
  }
  return path as AdminPath;
}

export async function admin<T>(path: AdminPath, body: Record<string, unknown> = {}): Promise<T> {
  // Who acts is the session's, never the body's: the gateway stamps humanId
  // and actor, a decision is the person's own press, and a key named
  // `department` is only ever the session's — a control names the
  // department it acts on as `name` (ADR-0073, ADR-0118).
  for (const key of ['humanId', 'actor', 'decidedByHuman', 'department']) {
    if (key in body) throw new Error(`a control never names "${key}"; the gateway stamps who acts from the session`);
  }
  let response: Response;
  try {
    response = await fetch(`/v1/admin${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
  } catch {
    throw new ApiError({ kind: 'offline' });
  }
  if (!response.ok) throw new ApiError(await failureFrom(response, path));
  return (await response.json()) as T;
}
